import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { PhotoSelect } from 'app/boot-trade-in-program/_forms/UpgradeBootForm';
import { LETTER_SIZES } from 'models/shared';
import FormLabel from '@c/forms/controls/FormLabel';
import FormSelect from '@c/forms/controls/FormSelect';
import { zodResolver } from '@hookform/resolvers/zod';
import FormInput from '@c/forms/controls/FormInput';
import { useAuth } from 'context/AuthContext';
import { useRouter } from '@bprogress/next';
import { useState } from 'react';
import { ChatInput } from 'models/chat';
import { createChat } from '@util/firestore/messages';
import { useChat } from 'context/ChatContext';

const formSchema = z.object({
  photos: z
    .array(
      z.object({
        full: z.string(),
        thumb: z.string(),
        mime_type: z.string().optional(),
        download_url: z.string().optional(),
      })
    )
    .min(3, 'Must have at least 3 photos'),
  currentSize: z.string().min(1, 'Please select a size'),
  request: z.string().min(1, 'Please enter a request'),
});

type Form = z.infer<typeof formSchema>;

type AektivTradeInFormProps = {
  dismiss: () => void;
};

export default function AektivTradeInForm({ dismiss }: AektivTradeInFormProps) {
  const { userDoc } = useAuth();
  const router = useRouter();
  const { openChatDrawer } = useChat();
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<Form>({
    defaultValues: {
      photos: [],
      request: '',
    },
    resolver: zodResolver(formSchema),
  });

  const {
    register,
    control,
    formState: { errors },
    setValue,
  } = form;

  async function onValid(formData: Form) {
    if (!userDoc) {
      router.push('/login');
      return;
    }

    const data = {
      currentSize: formData.currentSize,
      request: formData.request,
      photos: formData.photos,
    };

    setIsLoading(true);

    if (data.photos.length < 3) {
      form.setError('photos', {
        type: 'manual',
        message: 'Please upload at least 3 photos',
      });
      return;
    }

    const content = `New Gear Trade-In Request: Trading in size ${data.currentSize} | Requesting: ${data.request}`;

    const messages = [
      {
        uid: userDoc.uid,
        content: content,
        created_at: Date.now(),
        is_img: false,
        is_auto: false,
      },
      ...data.photos.map((photo) => ({
        uid: userDoc.uid,
        is_img: true,
        content: photo.full,
        thumbnail: photo.thumb,
        mime_type: photo.mime_type ?? '',
        download_url: photo.download_url ?? '',
        created_at: Date.now(),
        is_auto: false,
      })),
    ];

    const newChat: ChatInput = {
      id: '',
      buyer_id: 'FXK4OyXFHofIStk1I2YvG1TiPGm1', // AektivCo Account
      seller_id: userDoc.uid,
      uids: ['FXK4OyXFHofIStk1I2YvG1TiPGm1', userDoc.uid],
      last_time: Date.now(),
      created_at: Date.now(),
      messages,
      unread: {
        ['FXK4OyXFHofIStk1I2YvG1TiPGm1']: true,
      },
      from_web: true,
      flagged: false,
      is_expert: false,
      is_offer: false,
    };

    try {
      const chatDoc = await createChat(newChat);

      openChatDrawer(chatDoc);
      dismiss();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <form
      onSubmit={form.handleSubmit(onValid)}
      className="flex flex-col gap-y-8"
    >
      <div className="flex items-start gap-x-1">
        <span className="text-[1.8rem] font-semibold text-black">
          Upload photos of your gear
        </span>
        <span className="text-[1.8rem] font-semibold text-brand-secondary">
          *
        </span>
      </div>

      <div className="flex flex-col gap-y-8 sm:pr-[2rem]">
        <div className="flex items-center gap-x-6">
          <PhotoSelect form={form} />
        </div>

        <FormLabel
          value="What size is your gear?"
          required
          className="!text-[1.8rem]"
        >
          <FormSelect
            control={control}
            options={LETTER_SIZES.map((size: string) => ({
              id: size,
              label: size,
              value: size,
            }))}
            error={!!errors.currentSize}
            {...register('currentSize')}
            placeholder="Select size"
            className="!rounded-[1rem] !text-[2.1rem] !font-medium sm:!text-[1.8rem]"
            onChange={(option: any) => {
              setValue('currentSize', option.value);
            }}
          />
        </FormLabel>

        {errors.currentSize && (
          <span className="text-[1.4rem] text-red-500">
            {errors.currentSize.message}
          </span>
        )}

        <FormLabel
          value="What Aektiv gear you're looking for?"
          required
          className="!text-[1.8rem]"
        >
          <FormInput
            placeholder="What gear do you want?"
            {...register('request')}
            className="sm:!text-[1.7rem]"
          />
        </FormLabel>

        {errors.request && (
          <span className="text-[1.4rem] text-red-500">
            {errors.request.message}
          </span>
        )}

        <span className="text-[1.8rem] font-medium text-brand-secondary">
          *If gear arrives dirty or wet, they will be returned at your cost, and
          the trade-in will be invalid.
        </span>

        <button
          type="submit"
          className="flex h-[5rem] w-full items-center justify-center rounded-[1.5rem] bg-brand-secondary px-4 py-2 text-[2.1rem] font-semibold text-white sm:text-[1.8rem] sm:font-medium"
          disabled={isLoading}
        >
          {isLoading ? 'Loading...' : 'Get Offer'}
        </button>
      </div>
    </form>
  );
}
